// src/components/KeywordFilterControl.tsx - FIXED: Removed unused fieldDef parameter
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Plus, X } from 'lucide-react';
import { useAppContext, useUIConfig } from '../hooks/useAppContext';
import TagInput from './TagInput';

interface KeywordGroup {
  id: string;
  mode: 'AND' | 'OR' | 'EXCLUDE';
  keywords: string[];
  match_mode?: 'like' | 'exact';  // NEW: Support for like vs exact match
}

// Backend-specific interface without id field
interface BackendKeywordGroup {
  mode: 'AND' | 'OR' | 'EXCLUDE';
  keywords: string[];
  match_mode?: 'like' | 'exact';
}

interface GroupedKeywordFilter {
  type: 'keyword_group';
  groups: BackendKeywordGroup[];
}

interface ExistenceFilter {
  type: 'existence';
  has_value: boolean;
}

type KeywordFilterValue = GroupedKeywordFilter | ExistenceFilter | null;

interface KeywordFilterControlProps {
  fieldName: string;
}

interface PersistentState {
  mode: 'simple' | 'advanced';
  simpleKeywords: string[];
  advancedGroups: KeywordGroup[];
  existenceState: 'none' | 'has_data' | 'is_empty';
  matchMode: 'like' | 'exact';  // NEW: Track match mode
}

const KeywordFilterControl: React.FC<KeywordFilterControlProps> = ({ fieldName }) => {
  const { state, updateFilter } = useAppContext();
  const uiConfig = useUIConfig();
  
  const [persistentState, setPersistentState] = useState<PersistentState>({
    mode: 'simple',
    simpleKeywords: [],
    advancedGroups: [{ id: '1', mode: 'OR', keywords: [] }],
    existenceState: 'none',
    matchMode: 'like'  // Default to like mode
  });
  
  const isInitializedRef = useRef(false);
  const internalUpdateRef = useRef(false);
  const lastExternalFilterRef = useRef<string>('');
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const currentFilter = state.searchState.filters[fieldName] as KeywordFilterValue;
  const currentFilterString = JSON.stringify(currentFilter);

  const hasActiveFilters = useMemo(() => {
    return Object.keys(state.searchState.filters).length > 0;
  }, [state.searchState.filters]);

  const dynamicSuggestions = React.useMemo(() => {
    const fieldSuggestions = state.keywordSuggestions?.[fieldName] || [];
    return fieldSuggestions.map((suggestionValue: string) => ({
      value: suggestionValue,
      count: undefined
    }));
  }, [state.keywordSuggestions, fieldName]);

  useEffect(() => {
    if (internalUpdateRef.current) {
      internalUpdateRef.current = false;
      return;
    }

    if (currentFilterString === lastExternalFilterRef.current) {
      return;
    }

    console.log(`🔧 [${fieldName}] Syncing external filter to internal state:`, currentFilter);
    lastExternalFilterRef.current = currentFilterString;

    if (currentFilter) {
      if (currentFilter.type === 'existence') {
        setPersistentState(prev => ({
          ...prev,
          existenceState: currentFilter.has_value ? 'has_data' : 'is_empty'
        }));
      } else if (currentFilter.type === 'keyword_group' && currentFilter.groups.length > 0) {
        const groups = currentFilter.groups.map((g, i) => ({
          ...g,
          id: (g as { id?: string }).id || String(i + 1)
        }));

        const isSimple = groups.length === 1 && groups[0].mode === 'OR';
        const matchMode = groups[0].match_mode || 'like';  // Default to like if not specified
        
        setPersistentState(prev => ({
          ...prev,
          mode: isSimple ? 'simple' : 'advanced',
          simpleKeywords: isSimple ? [...groups[0].keywords] : [],
          advancedGroups: groups,
          existenceState: 'none',
          matchMode: matchMode
        }));
      }
    } else {
      console.log(`🧹 [${fieldName}] Filter cleared externally, resetting keywords`);
      setPersistentState(prev => ({
        ...prev,
        simpleKeywords: [],
        advancedGroups: [{ id: '1', mode: 'OR', keywords: [] }],
        existenceState: 'none'
      }));
    }

    isInitializedRef.current = true;
  }, [currentFilterString, fieldName, currentFilter]);

  const updateExternalFilter = useCallback((newFilter: KeywordFilterValue) => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      console.log(`📡 [${fieldName}] Applying filter with internal flag:`, newFilter);
      
      internalUpdateRef.current = true;
      lastExternalFilterRef.current = JSON.stringify(newFilter);
      
      updateFilter(fieldName, newFilter);
    }, 300);
  }, [fieldName, updateFilter]);

  const handleModeSwitch = useCallback((newMode: 'simple' | 'advanced') => {
    setPersistentState(prev => {
      if (newMode === prev.mode) return prev;
      
      console.log(`🔄 [${fieldName}] Switching mode: ${prev.mode} -> ${newMode}`);
      
      if (newMode === 'advanced') {
        return {
          ...prev,
          mode: 'advanced',
          advancedGroups: [{
            id: '1',
            mode: 'OR',
            keywords: [...prev.simpleKeywords],
            match_mode: prev.matchMode // preserve current match_mode
          }]
        };
      } else {
        const orKeywords = prev.advancedGroups
          .filter(g => g.mode === 'OR')
          .flatMap(g => g.keywords);
        
        return {
          ...prev,
          mode: 'simple',
          simpleKeywords: orKeywords
        };
      }
    });
    
    console.log(`✅ [${fieldName}] Mode switch completed`);
  }, [fieldName]);

  const handleSimpleChange = useCallback((tags: Array<{id: string; label: string; value: string}>) => {
    const keywords = tags.map(tag => tag.value);
    
    setPersistentState(prev => ({
      ...prev,
      simpleKeywords: keywords,
      existenceState: 'none'
    }));
    
    if (keywords.length === 0) {
      updateExternalFilter(null);
    } else {
      const filter: GroupedKeywordFilter = {
        type: 'keyword_group',
        groups: [{ mode: 'OR', keywords, match_mode: persistentState.matchMode }]
      };
      updateExternalFilter(filter);
    }
  }, [updateExternalFilter, persistentState.matchMode]);

  const updateGroup = useCallback((groupId: string, updates: Partial<KeywordGroup>) => {
    setPersistentState(prev => {
      const newGroups = prev.advancedGroups.map(g => 
        g.id === groupId ? { ...g, ...updates, match_mode: updates.match_mode ?? g.match_mode ?? 'like' } : g
      );
      
      const activeGroups = newGroups.filter(g => g.keywords.length > 0);
      if (activeGroups.length === 0) {
        updateExternalFilter(null);
      } else {
        const filter: GroupedKeywordFilter = {
          type: 'keyword_group',
          groups: newGroups.map(g => ({ mode: g.mode, keywords: g.keywords, match_mode: g.match_mode }))
        };
        updateExternalFilter(filter);
      }
      
      return { ...prev, advancedGroups: newGroups, existenceState: 'none' };
    });
  }, [updateExternalFilter]);

  const addGroup = useCallback(() => {
    const newGroup: KeywordGroup = {
      id: Date.now().toString(),
      mode: 'OR',
      keywords: [],
      match_mode: 'like', // Always default to 'like' for new groups
    };
    
    setPersistentState(prev => ({
      ...prev,
      advancedGroups: [...prev.advancedGroups, newGroup]
    }));
  }, []);

  const removeGroup = useCallback((groupId: string) => {
    setPersistentState(prev => {
      if (prev.advancedGroups.length <= 1) return prev;
      
      const newGroups = prev.advancedGroups.filter(g => g.id !== groupId);
      
      const activeGroups = newGroups.filter(g => g.keywords.length > 0);
      if (activeGroups.length === 0) {
        updateExternalFilter(null);
      } else {
        const filter: GroupedKeywordFilter = {
          type: 'keyword_group',
          groups: activeGroups.map(g => ({ mode: g.mode, keywords: g.keywords, match_mode: persistentState.matchMode }))
        };
        updateExternalFilter(filter);
      }
      
      return { ...prev, advancedGroups: newGroups };
    });
  }, [updateExternalFilter, persistentState.matchMode]);

  const handleExistenceToggle = useCallback((newState: 'has_data' | 'is_empty') => {
    setPersistentState(prev => {
      const finalState = prev.existenceState === newState ? 'none' : newState;
      
      if (finalState !== 'none') {
        const existenceFilter: ExistenceFilter = {
          type: 'existence',
          has_value: finalState === 'has_data'
        };
        updateExternalFilter(existenceFilter);
      } else {
        if (prev.mode === 'simple' && prev.simpleKeywords.length > 0) {
                  const filter: GroupedKeywordFilter = {
          type: 'keyword_group',
          groups: [{ mode: 'OR', keywords: prev.simpleKeywords, match_mode: prev.matchMode }]
        };
          updateExternalFilter(filter);
        } else if (prev.mode === 'advanced') {
          const activeGroups = prev.advancedGroups.filter(g => g.keywords.length > 0);
          if (activeGroups.length > 0) {
            const filter: GroupedKeywordFilter = {
              type: 'keyword_group',
              groups: activeGroups.map(g => ({ mode: g.mode, keywords: g.keywords, match_mode: prev.matchMode }))
            };
            updateExternalFilter(filter);
          } else {
            updateExternalFilter(null);
          }
        } else {
          updateExternalFilter(null);
        }
      }
      
      return { ...prev, existenceState: finalState };
    });
  }, [updateExternalFilter]);

  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="keyword-filter-control">

      <div className="filter-mode-toggle">
        <button
          type="button"
          className={`mode-toggle-button ${persistentState.mode === 'simple' ? 'active' : ''}`}
          onClick={() => handleModeSwitch('simple')}
        >
          <span>Simple</span>
        </button>
        <button
          type="button"
          className={`mode-toggle-button ${persistentState.mode === 'advanced' ? 'active' : ''}`}
          onClick={() => handleModeSwitch('advanced')}
        >
          <span>Advanced</span>
        </button>
      </div>

      {/* Only show overall Contains/Exact Match toggle in simple mode */}
      {persistentState.mode === 'simple' && (
        <div className="filter-mode-toggle">
          <button
            type="button"
            className={`mode-toggle-button ${persistentState.matchMode === 'like' ? 'active' : ''}`}
            onClick={() => {
              setPersistentState(prev => ({ ...prev, matchMode: 'like' }));
              // Immediately trigger search with new match mode
              if (persistentState.simpleKeywords.length > 0) {
                const filter: GroupedKeywordFilter = {
                  type: 'keyword_group',
                  groups: [{ mode: 'OR', keywords: persistentState.simpleKeywords, match_mode: 'like' }]
                };
                updateExternalFilter(filter);
              }
            }}
          >
            <span>Contains</span>
          </button>
          <button
            type="button"
            className={`mode-toggle-button ${persistentState.matchMode === 'exact' ? 'active' : ''}`}
            onClick={() => {
              setPersistentState(prev => ({ ...prev, matchMode: 'exact' }));
              // Immediately trigger search with new match mode
              if (persistentState.simpleKeywords.length > 0) {
                const filter: GroupedKeywordFilter = {
                  type: 'keyword_group',
                  groups: [{ mode: 'OR', keywords: persistentState.simpleKeywords, match_mode: 'exact' }]
                };
                updateExternalFilter(filter);
              }
            }}
          >
            <span>Exact Match</span>
          </button>
        </div>
      )}

      <div className={`keyword-controls ${persistentState.existenceState !== 'none' ? 'existence-mode' : ''}`}>
        
        {persistentState.mode === 'simple' && (
          <div className="simple-mode">
            <TagInput
              key={`simple-${fieldName}`}
              tags={persistentState.simpleKeywords.map((kw, i) => ({ id: String(i), label: kw, value: kw }))}
              onTagsChange={handleSimpleChange}
              placeholder="Add keywords…"
              suggestions={dynamicSuggestions}
              className="keyword-tag-input"
              isLoading={false}
              showCounts={!hasActiveFilters}
              maxSuggestions={uiConfig.maxSuggestions}
              fieldName={fieldName}
            />
          </div>
        )}

        {persistentState.mode === 'advanced' && (
          <div className="advanced-mode">
            {persistentState.advancedGroups.map((group) => (
              <div key={group.id} className="keyword-group">
                <div className="group-header">
                  <select
                    value={group.mode}
                    onChange={(e) => updateGroup(group.id, { mode: e.target.value as 'AND' | 'OR' | 'EXCLUDE' })}
                    className="group-mode-select"
                  >
                    <option value="OR">OR (any keyword)</option>
                    <option value="AND">AND (all keywords)</option>
                    <option value="EXCLUDE">EXCLUDE (none of these)</option>
                  </select>
                  {persistentState.advancedGroups.length > 1 && (
                    <button
                      type="button"
                      className="remove-group-button"
                      onClick={() => removeGroup(group.id)}
                      title="Remove group"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                {/* Per-group Contains/Exact Match toggle */}
                <div className="filter-mode-toggle" style={{ marginBottom: 8 }}>
                  <button
                    type="button"
                    className={`mode-toggle-button ${group.match_mode !== 'exact' ? 'active' : ''}`}
                    onClick={() => {
                      updateGroup(group.id, { match_mode: 'like' });
                      // The updateGroup function already triggers updateExternalFilter
                    }}
                  >
                    <span>Contains</span>
                  </button>
                  <button
                    type="button"
                    className={`mode-toggle-button ${group.match_mode === 'exact' ? 'active' : ''}`}
                    onClick={() => {
                      updateGroup(group.id, { match_mode: 'exact' });
                      // The updateGroup function already triggers updateExternalFilter
                    }}
                  >
                    <span>Exact Match</span>
                  </button>
                </div>
                <TagInput
                  key={`advanced-${group.id}`}
                  tags={group.keywords.map((kw, i) => ({ id: String(i), label: kw, value: kw }))}
                  onTagsChange={(tags) => updateGroup(group.id, { keywords: tags.map(t => t.value) })}
                  placeholder="Add keywords…"
                  suggestions={dynamicSuggestions}
                  className="group-tag-input"
                  showCounts={!hasActiveFilters}
                  maxSuggestions={uiConfig.maxSuggestions}
                  fieldName={fieldName}
                />
              </div>
            ))}
            <button
              type="button"
              className="add-group-button"
              onClick={addGroup}
            >
              <Plus size={14} />
              <span>Add Group</span>
            </button>
          </div>
        )}
      </div>

      <div className="existence-toggle-buttons">
        <button
          type="button"
          className={`existence-toggle-btn ${persistentState.existenceState === 'has_data' ? 'active' : ''}`}
          onClick={() => handleExistenceToggle('has_data')}
        >
          Has Data
        </button>
        <button
          type="button"
          className={`existence-toggle-btn ${persistentState.existenceState === 'is_empty' ? 'active' : ''}`}
          onClick={() => handleExistenceToggle('is_empty')}
        >
          Is Empty
        </button>
      </div>
    </div>
  );
};

export default KeywordFilterControl;